import type { ReactNode } from 'react'
import type { ApiFehler } from './api'

/** Eine abgesetzte Flaeche mit Ueberschrift. */
export function Karte({
  titel,
  handlung,
  children,
}: {
  titel?: string
  handlung?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="karte">
      {(titel || handlung) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {titel && <h2>{titel}</h2>}
          {handlung}
        </div>
      )}
      {children}
    </section>
  )
}

/** Eine Kennzahl fuer die Uebersicht. */
export function Kennzahl({
  beschriftung,
  wert,
  fussnote,
}: {
  beschriftung: string
  wert: ReactNode
  fussnote?: string
}) {
  return (
    <div className="kennzahl">
      <div className="beschriftung">{beschriftung}</div>
      <div className="wert">{wert}</div>
      {fussnote && (
        <div style={{ fontSize: 12, color: 'var(--gedaempft)' }}>{fussnote}</div>
      )}
    </div>
  )
}

/** Ein beschriftetes Feld in einer Angabenliste. */
export function Angabe({ beschriftung, children }: { beschriftung: string; children: ReactNode }) {
  return (
    <div className="angabe">
      <div className="beschriftung">{beschriftung}</div>
      <div className="wert">{children ?? '—'}</div>
    </div>
  )
}

/** Ein farbiges Merkmal, etwa ein Status; `titel` erscheint beim Zeigen darauf. */
export function Merkmal({
  art = 'neutral',
  titel,
  children,
}: {
  art?: 'neutral' | 'gut' | 'warnung' | 'fehler' | 'betont'
  titel?: string
  children: ReactNode
}) {
  return (
    <span className={`merkmal ${art === 'neutral' ? '' : art}`} title={titel}>
      {children}
    </span>
  )
}

/** Zeigt Ladezustand und Fehler einheitlich an. */
export function Zustand({
  laeuft,
  fehler,
  leer,
  leertext = 'Keine Einträge vorhanden.',
  children,
}: {
  laeuft: boolean
  fehler?: ApiFehler
  leer?: boolean
  leertext?: string
  children: ReactNode
}) {
  if (fehler) {
    return <Fehleranzeige fehler={fehler} />
  }
  if (laeuft) {
    return <div className="laedt">Wird geladen …</div>
  }
  if (leer) {
    return <div className="leer">{leertext}</div>
  }
  return <>{children}</>
}

/** Stellt einen Problembericht der Anwendung dar. */
export function Fehleranzeige({ fehler }: { fehler: ApiFehler }) {
  const art = fehler.status === 404 ? 'warnung' : 'fehler'
  return (
    <div className={`hinweis ${art}`}>
      <strong>{fehler.bericht.title ?? 'Fehler'}</strong>
      {fehler.bericht.detail && <div>{fehler.bericht.detail}</div>}
      {fehler.bericht.beanstandungen && fehler.bericht.beanstandungen.length > 0 && (
        <ul>
          {fehler.bericht.beanstandungen.map((eintrag) => (
            <li key={eintrag}>{eintrag}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Ein einfacher Balkenverlauf, etwa fuer einen Tageslastgang. */
export function Verlauf({
  werte,
  beschriftungLinks,
  beschriftungRechts,
}: {
  werte: number[]
  beschriftungLinks: string
  beschriftungRechts: string
}) {
  const hoechstwert = Math.max(...werte, 0.0001)
  return (
    <div>
      <div className="balken">
        {werte.map((wert, stelle) => (
          <div
            key={stelle}
            style={{ height: `${Math.max((wert / hoechstwert) * 100, 0.5)}%` }}
            title={`${stelle}: ${wert}`}
          />
        ))}
      </div>
      <div className="balkenfuss">
        <span>{beschriftungLinks}</span>
        <span>{beschriftungRechts}</span>
      </div>
    </div>
  )
}

/** Eine Tabelle mit Kopfzeile; numerische Spalten werden rechts gesetzt. */
export function Tabelle({
  spalten,
  children,
}: {
  spalten: { name: string; zahl?: boolean }[]
  children: ReactNode
}) {
  return (
    <div className="tabellenrahmen">
      <table>
        <thead>
          <tr>
            {spalten.map((spalte) => (
              <th key={spalte.name} className={spalte.zahl ? 'zahl' : undefined}>
                {spalte.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}
